import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Modal } from '../modal'
import { Button } from '../button'
import { Spinner } from '../spinner'
import { useToast } from '../toast'
import { reservationApi } from '../../services/reservations/api'
import { reservationQueries } from '../../services/reservations/queries'
import { ReservationGuest, TraStatus } from '../../services/reservation-guests/models'

const guestName = (g: ReservationGuest) => `${g.guestRef.firstName} ${g.guestRef.lastName}`

interface Props {
  isOpen: boolean
  reservationId: string
  guests: ReservationGuest[]
  onClose: () => void
  onError: (message: string) => void
}

/** Confirms the reservation-level purge: scrubs every unscrubbed guest. Warns about
 * pending SIRE data and about filed guests, whose filing records survive the scrub. */
export function PurgeGuestDataDialog({ isOpen, reservationId, guests, onClose, onError }: Props) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const unscrubbed = guests.filter((g) => !g.dataScrubbed)
  const unsubmittedSireGuests = unscrubbed.filter((g) => g.needsSire && !g.sireSubmitted)
  const filedGuests = unscrubbed.filter((g) => g.traStatus === TraStatus.success || g.sireSubmitted)

  const { mutate: purge, isPending } = useMutation({
    mutationFn: () => reservationApi.csc.purgeGuestData(reservationId),
    onSuccess: (result) => {
      onClose()
      queryClient.invalidateQueries({ queryKey: reservationQueries.all() })
      if (result.status === 'partial') {
        toast({
          type: 'warning',
          title: t('reservations.purgePartialTitle'),
          description: t('reservations.purgePartialMessage', {
            scrubbed: result.scrubbedCount,
            failed: result.failedCount,
          }),
        })
      }
    },
    onError: (e: any) => {
      onClose()
      onError(e.message || 'Error purging guest data')
    },
  })

  return (
    <Modal title={t('reservations.purgeGuestData')} isOpen={isOpen} onClose={onClose} size="sm">
      <div className="flex flex-col gap-4" data-testid="purge-confirm-dialog">
        {unsubmittedSireGuests.length > 0 && (
          <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
            {t('reservations.purgeSireWarning', {
              names: unsubmittedSireGuests.map(guestName).join(', '),
            })}
          </div>
        )}
        {filedGuests.length > 0 && (
          <div
            className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800"
            data-testid="purge-filed-warning"
          >
            {t('reservations.purgeFiledWarning')}
            <ul className="mt-2 list-disc pl-5">
              {filedGuests.map((g) => (
                <li key={g.id}>{guestName(g)}</li>
              ))}
            </ul>
          </div>
        )}
        <p className="text-sm text-text-secondary">{t('reservations.purgeConfirmMessage')}</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => purge()}
            disabled={isPending}
            data-testid="purge-confirm-button"
          >
            {isPending ? (
              <span className="flex items-center gap-2">
                <Spinner size="xs" /> {t('reservations.purging')}
              </span>
            ) : (
              t('reservations.purgeData')
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
